import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'

/**
 * Fixed left section rail. It occupies the left column of the nav's 1440 zone
 * (so its left edge lines up with the logo) while the section content right-
 * aligns beside it via `.container-rail`. It is a scroll-spy that highlights the
 * section crossing the viewport middle and flips its own text colour light/dark
 * over `data-nav-dark` backdrops (same adaptive trick the top nav uses).
 *
 * It only reveals once the Why section reaches the viewport middle and hides
 * again past Customers, so it never sits over the hero or the closing CTA. Shown
 * only at >=1440 where the zone has room to hold it clear of the content.
 */
const SECTIONS = [
  { id: 'why', label: 'Why LangWatch' },
  { id: 'platform', label: 'Platform' },
  { id: 'langy', label: 'Langy' },
  { id: 'enterprise', label: 'Enterprise' },
  { id: 'customers', label: 'Customers' },
]

/** True when the viewport point (x, y) sits over any `data-nav-dark` region. */
function pointOverDark(x: number, y: number): boolean {
  const darks = document.querySelectorAll<HTMLElement>('[data-nav-dark]')
  for (const d of darks) {
    const r = d.getBoundingClientRect()
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true
  }
  return false
}

export function SectionRail() {
  const navRef = useRef<HTMLElement>(null)
  const [active, setActive] = useState(-1)
  const [visible, setVisible] = useState(false)
  const [dark, setDark] = useState(false)

  useEffect(() => {
    let raf = 0
    const update = () => {
      const ih = window.innerHeight
      const midY = ih / 2

      let next = -1
      for (let i = 0; i < SECTIONS.length; i++) {
        const el = document.getElementById(SECTIONS[i].id)
        if (!el) continue
        const r = el.getBoundingClientRect()
        if (r.top <= midY && r.bottom > midY) next = i
      }
      setActive(next)

      // Reveal once the Why heading crosses the middle of the viewport and hide
      // again past Customers.
      const headR = document.getElementById('why-heading')?.getBoundingClientRect()
      const custR = document.getElementById('customers')?.getBoundingClientRect()
      const inZone = !!headR && !!custR && headR.top <= midY && custR.bottom > midY

      // Hide while a carousel next to the rail is scrolled left, so its cards
      // never slide under the rail. It comes back when that row scrolls back to
      // the start, or when a different (unscrolled) row moves into view.
      const band = 140 // roughly the rail's vertical half-height
      let coveredByCarousel = false
      for (const row of document.querySelectorAll<HTMLElement>('[data-carousel-row]')) {
        const r = row.getBoundingClientRect()
        const overlapsRail = r.top < midY + band && r.bottom > midY - band
        if (overlapsRail && row.scrollLeft > 16) {
          coveredByCarousel = true
          break
        }
      }

      setVisible(inZone && !coveredByCarousel)

      const el = navRef.current
      if (el) {
        const r = el.getBoundingClientRect()
        setDark(pointOverDark(r.left + r.width / 2, midY))
      }
    }
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    // Also react to the carousels scrolling horizontally (which does not fire a
    // window scroll), so the rail hides/shows as their cards move past it.
    const rows = Array.from(
      document.querySelectorAll<HTMLElement>('[data-carousel-row]'),
    )
    rows.forEach((row) => row.addEventListener('scroll', onScroll, { passive: true }))
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      rows.forEach((row) => row.removeEventListener('scroll', onScroll))
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <nav
      ref={navRef}
      aria-label="Sections"
      className={cn(
        'fixed top-1/2 z-30 hidden -translate-y-1/2 pl-6 transition-opacity duration-[250ms] [@media(min-width:1440px)]:block',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      style={{ left: 'calc((100vw - 1440px) / 2 + 40px)' }}
    >
      <ul className="flex flex-col gap-4">
        {SECTIONS.map((s, i) => {
          const isActive = i === active
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => {
                  // Native hash navigation on the home page ends up back at the
                  // top (header) instead of the section, so scroll to the target
                  // ourselves, offset for the sticky header. Modified clicks and
                  // middle-click still fall through to the href.
                  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                  const el = document.getElementById(s.id)
                  if (!el) return
                  e.preventDefault()
                  const top = el.getBoundingClientRect().top + window.scrollY - 88
                  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
                }}
                className={cn(
                  'whitespace-nowrap text-[13.5px] transition-colors duration-300',
                  isActive ? 'font-semibold' : 'font-medium',
                  dark
                    ? isActive
                      ? 'text-white'
                      : 'text-white/35 hover:text-white/70'
                    : isActive
                      ? 'text-ink-900'
                      : 'text-ink-400 hover:text-ink-600',
                )}
              >
                {s.label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
